import {useCallback, useEffect, useRef, useState} from 'react'

/**
 * IS THE LOCATION GRANT "PRECISE" OR "APPROXIMATE"?
 *
 * Mobile Chrome (Android 12+) and iOS Safari let the user pick "Approximate" in the
 * permission dialog. No web API can hide that option or force "Precise", so the only thing
 * a page can do is detect it afterwards and ask the user to change it.
 *
 * ⚠️ DETECTED FROM `coords.accuracy` — see APPROXIMATE_ACCURACY_M and `check` below.
 *
 * ⚠️ MOBILE ONLY. A desktop has no GPS and routinely reports a wifi/IP fix of 1km+, which
 * would read as "approximate" when the user chose nothing of the kind.
 *
 * ⚠️ NEVER RAISES THE PROMPT. It checks only once the permission is already 'granted' —
 * asking is left to the screens that actually need a position.
 *
 * Re-checked on permission change and whenever the tab becomes visible again, which is
 * the moment a user comes back from changing the setting.
 *
 * `status`: 'unknown' | 'precise' | 'approximate'
 */

/**
 * An approximate grant is never reported better than this.
 *
 * ⚠️ 2000m, NOT a "looks rough" number. Android's coarse location is fudged to a ~2km grid
 * and its reported accuracy is floored at 2000m; iOS approximate is coarser still. A
 * PRECISE grant can also produce a rough first fix (indoors, GPS still cold, network
 * fix only) — a lower threshold read that as "approximate" and blocked a driver who had
 * chosen Precise.
 */
export const APPROXIMATE_ACCURACY_M = 2000

/**
 * How long to watch before concluding "approximate". A precise grant usually improves
 * within seconds as GPS/wifi kick in; an approximate one never gets under the floor.
 */
const WATCH_MS = 15000

/**
 * ⚠️ THE WRAPPER CONDITION: "approximate" must be seen this many windows IN A ROW before the
 * screen shows. One window was enough before, and a slow first fix after a reload (GPS cold,
 * network fix only) failed it — the screen flashed up and the next check removed it. Slower
 * to appear (~30s), but it only appears when it is true. Any precise fix resets the count.
 */
const CONFIRM_WINDOWS = 2

export const isMobileDevice = () =>
    typeof navigator !== 'undefined'
    && (navigator.userAgentData?.mobile || /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent))

export function usePreciseLocation({enabled = true, granted = false} = {}) {
    const [status, setStatus] = useState('unknown')
    const watchRef = useRef(null)
    const timerRef = useRef(null)
    const streakRef = useRef(0)
    const checkRef = useRef(null)

    const active = enabled && isMobileDevice() && typeof navigator !== 'undefined' && !!navigator.geolocation

    const stop = useCallback(() => {
        if (watchRef.current !== null) navigator.geolocation.clearWatch(watchRef.current)
        clearTimeout(timerRef.current)
        watchRef.current = null
    }, [])

    /**
     * WATCHES for a while and keeps the BEST accuracy seen, rather than judging one fix.
     * Any fix under the floor proves "precise" at once; only a whole window of fixes that
     * never get under it reads as "approximate". No fix at all changes nothing.
     */
    const check = useCallback(() => {
        if (!active) return
        stop()
        let best = Infinity

        watchRef.current = navigator.geolocation.watchPosition(
            (pos) => {
                const accuracy = pos?.coords?.accuracy
                if (!Number.isFinite(accuracy)) return
                best = Math.min(best, accuracy)
                if (best < APPROXIMATE_ACCURACY_M) {
                    streakRef.current = 0
                    setStatus('precise')
                    stop()
                }
            },
            () => {},
            {enableHighAccuracy: true, maximumAge: 0},
        )
        timerRef.current = setTimeout(() => {
            stop()
            if (best === Infinity) return
            if (best < APPROXIMATE_ACCURACY_M) {
                streakRef.current = 0
                setStatus('precise')
                return
            }
            streakRef.current += 1
            if (streakRef.current >= CONFIRM_WINDOWS) setStatus('approximate')
            else checkRef.current?.()   // not confirmed yet — watch another window
        }, WATCH_MS)
    }, [active, stop])
    checkRef.current = check

    useEffect(() => stop, [stop])

    /* ⚠️ ALSO STARTED FROM `granted` — a fix that actually arrived (utils/locationSignal),
       not only the Permissions API below. On iOS Safari that API reports "prompt" for an
       allowed site (or does not exist), so the check below never started there and an
       Approximate iPhone was never caught. */
    const grantedRef = useRef(granted)
    grantedRef.current = granted
    useEffect(() => {
        if (active && granted) check()
    }, [active, granted, check])

    useEffect(() => {
        if (!active || !navigator.permissions?.query) return undefined
        let cancelled = false
        let permission = null

        const onPermission = () => {
            if (cancelled) return
            if (permission.state === 'granted') check()
            else {
                streakRef.current = 0
                setStatus('unknown')
            }
        }
        const onVisible = () => {
            if (document.visibilityState === 'visible' && (permission?.state === 'granted' || grantedRef.current)) check()
        }

        navigator.permissions.query({name: 'geolocation'})
            .then((result) => {
                if (cancelled) return
                permission = result
                result.addEventListener('change', onPermission)
                onPermission()
            })
            .catch(() => {})
        document.addEventListener('visibilitychange', onVisible)

        return () => {
            cancelled = true
            permission?.removeEventListener('change', onPermission)
            document.removeEventListener('visibilitychange', onVisible)
        }
    }, [active, check])

    return {status, isApproximate: active && status === 'approximate', check}
}

export default usePreciseLocation
